import * as React from 'react';

import {
  Box, Divider, IconButton, Paper, Tooltip, Typography,
} from '@mui/material';

import { title } from 'process';
import _kebabCase from 'lodash/kebabCase';
import { DateTime } from 'luxon';
import { CalendarIcon, DatePicker } from '@mui/x-date-pickers';
import { InfoOutlined } from '@mui/icons-material';
import { UnattachedEventReportsProvider } from '../../core/context/UnattachedEventReportsContext';
import { UnattachedEventReportsSelectionProvider } from '../../core/context/UnattachedEventReportsSelectionContext';

import UnattachedEventReportsWrapper from './UnattachedEventReportsWrapper';
import UnattachedEventReportsGroupedList from './UnattachedEventReportsGroupedList';
import UnattachedEventReportsIndividualList from './UnattachedEventReportsIndividualList';

import FormPaper from '../FormPaper';
import SvgIconHelpDocument from '../../assets/svg/SvgIconHelpDocument';
import { Preference, usePreferences } from '../../core/context/PreferencesContext';

interface UnattachedEventReportsProps {
  color: string | null | undefined;
  hidden?: boolean;
}

function UnattachedEventReports({ color, hidden }: UnattachedEventReportsProps) {
  const data = usePreferences();

  const handleUpdateCutoffDate = (date?: string) => {
    if (date) {
      data.updatePreference(Preference.IncidentUnattachedCutoffDate, date);
    }
  };

  if (hidden) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <React.Fragment />;
  }

  return (
    <FormPaper color={color}>
      <Box display="flex" alignItems="center" sx={{ mt: 1, mb: 2 }}>
        <Box display="flex" alignItems="center" flexGrow={1}>
          <Typography sx={{ mr: 2 }} variant="h5">
            Un-Processed Reports
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-evenly" mr={3} maxWidth={170}>
          <Box display="flex" mr={1} alignItems="center">
            <Tooltip title="This field filters out all reports prior to the selected date."><InfoOutlined /></Tooltip>
          </Box>
          <Box>
            <DatePicker
              value={DateTime.fromISO(data.incidentUnattachedCutoffDate)}
              onChange={(date) => handleUpdateCutoffDate(date?.toISO() ?? undefined)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'standard',
                },
              }}
            />
          </Box>
        </Box>
        <IconButton
          href="api/metadata/help?formName=incident-help.pdf"
          target="_blank"
          sx={{ mr: 3 }}
        >
          <SvgIconHelpDocument />
        </IconButton>
      </Box>
      <Paper variant="outlined" sx={{ mt: 0, mb: 1 }}>
        <UnattachedEventReportsProvider cutoffDate={data.incidentUnattachedCutoffDate}>
          <UnattachedEventReportsSelectionProvider>
            <UnattachedEventReportsWrapper>
              <UnattachedEventReportsGroupedList />
              <Divider orientation="horizontal" />
              <UnattachedEventReportsIndividualList />
            </UnattachedEventReportsWrapper>
          </UnattachedEventReportsSelectionProvider>
        </UnattachedEventReportsProvider>
      </Paper>
    </FormPaper>
  );
}

export default UnattachedEventReports;
